import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
    LogOut, Menu, X, Search, FileText, Calendar, User,
    ScrollText, Shield, Gavel, Heart, Download, Star, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentList = () => {
    const { user, logout } = useAuth();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [docTypeFilter, setDocTypeFilter] = useState('all');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        fetchDocuments();
    }, [docTypeFilter]);

    const fetchDocuments = async () => {
        try {
            const params = {};
            if (docTypeFilter !== 'all') {
                params.document_type = docTypeFilter;
            }
            const response = await axios.get(`${API_URL}/api/documents`, { params });
            setDocuments(response.data);
        } catch (error) {
            toast.error('Failed to load documents');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const datePart = date.toLocaleDateString('en-US', {
            timeZone: 'America/New_York',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
        const timePart = date.toLocaleTimeString('en-US', {
            timeZone: 'America/New_York',
            hour: '2-digit',
            minute: '2-digit'
        });
        return `${datePart} ${timePart}`;
    };

    const deleteDoc = async (docId, docTitle, e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!window.confirm('Delete document "' + docTitle + '"? This cannot be undone.')) return;
        try {
            await axios.delete(API_URL + '/api/documents/' + docId);
            toast.success('Document deleted');
            fetchDocuments();
        } catch (error) {
            if (error.response?.data?.detail) {
                toast.error(error.response.data.detail);
            } else {
                toast.error('Failed to delete document');
            }
        }
    };

    const toggleFinalize = async (docId, e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            await axios.patch(API_URL + '/api/documents/' + docId + '/finalize');
            fetchDocuments();
        } catch (error) {
            toast.error('Failed to update document');
        }
    };

    const docTypeIcons = {
        will: ScrollText,
        trust: Shield,
        poa: Gavel,
        living_will: FileText,
        letter: FileText
    };

    const docTypeLabels = {
        will: 'Last Will & Testament',
        trust: 'Revocable Living Trust',
        poa: 'Power of Attorney',
        living_will: 'Living Will / Advance Directive',
        letter: 'Estate Planning Letter'
    };

    return (
        <div className="min-h-screen bg-[#FDFCF8]">
            {/* Header */}
            <header className="sticky top-0 z-50 border-b border-stone-200 bg-white/80 backdrop-blur-sm">
                <div className="flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
                    <Link to="/dashboard" className="flex items-center gap-3">
                        <img src="/lion-logo.png" alt="BestWillsAndTrusts" className="h-10 w-10 object-contain" />
                        <span className="font-serif text-xl font-semibold hidden sm:block text-amber-600">BestWillsAndTrusts</span>
                    </Link>
                    
                    <nav className="hidden md:flex items-center gap-8">
                        <Link to="/dashboard" className="text-sm font-medium text-muted-foreground hover:text-foreground">Dashboard</Link>
                        <Link to="/clients" className="text-sm font-medium text-muted-foreground hover:text-foreground">Clients</Link>
                        <Link to="/documents" className="text-sm font-medium text-primary">Documents</Link>
                        <Link to="/templates" className="text-sm font-medium text-muted-foreground hover:text-foreground">Templates</Link>
                        {user?.role === 'admin' && <Link to="/admin/users" className="text-sm font-medium text-muted-foreground hover:text-foreground">Users</Link>}
                    </nav>

                    <div className="flex items-center gap-4">
                        <span className="text-sm text-muted-foreground hidden sm:block">{user?.name}</span>
                        <Button variant="ghost" size="sm" onClick={logout} className="rounded-none">
                            <LogOut className="h-4 w-4 mr-2" />
                            <span className="hidden sm:inline">Sign Out</span>
                        </Button>
                        <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </Button>
                    </div>
                </div>
                
                {mobileMenuOpen && (
                    <motion.nav 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="md:hidden border-t border-stone-200 bg-white px-6 py-4 space-y-4"
                    >
                        <Link to="/dashboard" className="block text-sm font-medium text-muted-foreground">Dashboard</Link>
                        <Link to="/clients" className="block text-sm font-medium text-muted-foreground">Clients</Link>
                        <Link to="/documents" className="block text-sm font-medium text-primary">Documents</Link>
                    </motion.nav>
                )}
            </header>

            {/* Main content */}
            <main className="max-w-7xl mx-auto px-6 py-8">
                {/* Page header */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <h1 className="font-serif text-3xl lg:text-4xl mb-2">Documents</h1>
                    <p className="text-muted-foreground">
                        View and manage generated legal documents
                    </p>
                </motion.div>

                {/* Filter */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="mb-6"
                >
                    <div className="flex flex-wrap gap-4">
                        <Select value={docTypeFilter} onValueChange={setDocTypeFilter}>
                            <SelectTrigger className="w-64 rounded-none" data-testid="doc-type-filter">
                                <SelectValue placeholder="Filter by type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Document Types</SelectItem>
                                <SelectItem value="will">Last Will & Testament</SelectItem>
                                <SelectItem value="trust">Revocable Living Trust</SelectItem>
                                <SelectItem value="poa">Power of Attorney</SelectItem>
                                <SelectItem value="living_will">Living Will</SelectItem>
                                <SelectItem value="letter">Estate Planning Letter</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </motion.div>

                {/* Document list */}
                {loading ? (
                    <div className="space-y-4">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="h-24 bg-stone-100 animate-pulse rounded-none" />
                        ))}
                    </div>
                ) : documents.length > 0 ? (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="space-y-4"
                    >
                        {documents.map((doc, index) => {
                            const Icon = docTypeIcons[doc.document_type] || FileText;
                            return (
                                <motion.div
                                    key={doc.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 * index }}
                                >
                                    <Card className={'rounded-none transition-colors card-hover ' + (doc.finalized ? 'border-amber-400 bg-amber-50/50' : 'border-stone-200 hover:border-primary/30')}>
                                        <CardContent className="p-0">
                                            <div className="flex items-center justify-between p-6">
                                                <Link 
                                                    to={`/documents/${doc.id}`}
                                                    className="flex items-center gap-4 flex-1"
                                                    data-testid={`document-row-${doc.id}`}
                                                >
                                                    <div className={'p-3 ' + (doc.finalized ? 'bg-amber-100' : 'bg-primary/10')}>
                                                        {doc.finalized ? <Star className="h-6 w-6 text-amber-600 fill-amber-500" /> : <Icon className="h-6 w-6 text-primary" />}
                                                    </div>
                                                    <div>
                                                        <h3 className="font-serif text-lg font-medium mb-1">
                                                            {doc.title}
                                                            {doc.finalized && <span className="ml-2 text-sm text-amber-600 font-medium">• Final Version</span>}
                                                        </h3>
                                                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                                                            <span className="flex items-center gap-1">
                                                                <User className="h-3.5 w-3.5" />
                                                                {doc.client_name}
                                                            </span>
                                                            <span className="flex items-center gap-1">
                                                                <Calendar className="h-3.5 w-3.5" />
                                                                {formatDate(doc.created_at)}
                                                            </span>
                                                            <span>Version {doc.version}</span>
                                                        </div>
                                                    </div>
                                                </Link>
                                                <div className="flex items-center gap-2">
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon" 
                                                        className={'h-9 w-9 ' + (doc.finalized ? 'text-amber-600' : 'text-muted-foreground')}
                                                        onClick={(e) => toggleFinalize(doc.id, e)}
                                                        title={doc.finalized ? 'Unmark as final' : 'Mark as final version'}
                                                    >
                                                        <Star className={'h-5 w-5 ' + (doc.finalized ? 'fill-amber-500' : '')} />
                                                    </Button>
                                                    <Link to={`/documents/${doc.id}`}>
                                                        <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground">
                                                            <Download className="h-5 w-5" />
                                                        </Button>
                                                    </Link>
                                                    {!doc.finalized && (
                                                        <Button 
                                                            variant="ghost" 
                                                            size="icon" 
                                                            className="h-9 w-9 text-destructive hover:bg-destructive/10"
                                                            onClick={(e) => deleteDoc(doc.id, doc.title, e)}
                                                        >
                                                            <Trash2 className="h-5 w-5" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                ) : (
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-center py-16"
                    >
                        <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
                        <h2 className="font-serif text-2xl mb-2">No documents found</h2>
                        <p className="text-muted-foreground mb-6">
                            {docTypeFilter !== 'all' ? 'Try a different filter' : 'Generate documents from client profiles'}
                        </p>
                        <Link to="/clients">
                            <Button className="rounded-none">
                                View Clients
                            </Button>
                        </Link>
                    </motion.div>
                )}
            </main>
        </div>
    );
};

export default DocumentList;
